import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import { bindAgentStore } from "../utils/agent";
import { setUserProfile } from "@reducers/user/userSlice";
import { readPermissionCache, getUserId } from "@utils/permissionCache";
import { permissionCacheMiddleware } from "./permissionCacheMiddleware";
import { apiMiddlewares } from "./servicesMiddlewares";

/** Avoid default-export interop issues (e.g. Vite 8) with redux-persist/lib/storage. */
function getBrowserLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const storage = {
  getItem(key: string) {
    const ls = getBrowserLocalStorage();
    return Promise.resolve(ls?.getItem(key) ?? null);
  },
  setItem(key: string, value: string) {
    const ls = getBrowserLocalStorage();
    ls?.setItem(key, value);
    return Promise.resolve();
  },
  removeItem(key: string) {
    const ls = getBrowserLocalStorage();
    ls?.removeItem(key);
    return Promise.resolve();
  },
};
import rootReducer from "../reducers";
import "@services/adminApi";
import "@services/emailApi";
import "@services/notificationTemplateApi";
import "@services/faqApi";
import "@services/blogApi";
import "@services/bannerApi";
import "@services/cmsMenuApi";
import "@services/reportIssueApi";
import "@services/contactEnquiryApi";
import "@services/tripCancelManagerApi";
import "@services/promoCodeApi";
import "@services/stateManagementApi";
import "@services/geoFenceApi";
import "@services/chatAdminApi";
import "@services/languageApi";
import "@services/pageApi";
import "@services/settingApi";
import "@services/userApi";
import "@services/vehicleTypeApi";
import "@services/fuelTypeApi";
import "@services/rideTypeApi";
import "@services/vehicleCatalogApi";
import "@services/rideCategoryApi";
import "@services/driverApi";
import "@services/vehicleApi";
import "@services/dashboardApi";
import "@services/reviewApi";
import "@services/safetyApi";
import "@services/driverReportApi";
import "@services/transactionApi";
import "@services/withdrawalApi";
const persistConfig = {
  key: "tov-ride-admin-root",
  storage,
  whitelist: ["authReducer", "user", "theme", "saveFilter"],
};

const persistedRootReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(permissionCacheMiddleware)
      .concat(...apiMiddlewares),
});

bindAgentStore(store);

export const persistor = persistStore(store, undefined, () => {
  const u = store.getState().user.user;
  const id = u ? getUserId(u) : undefined;
  if (
    u &&
    id &&
    (u.permission === undefined || u.permission === null)
  ) {
    const cached = readPermissionCache(id);
    if (cached) {
      store.dispatch(setUserProfile({ ...u, permission: cached }));
    }
  }
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
